<script lang="ts">
	import { Icon, LabelListV2, LiveIndicator } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import {
		getCameraComponentKey,
		getCameraStatusLabelList,
		isCameraPrivacyEnabled,
	} from 'src/components/Camera/helpers/camera-helpers';
	import { computeCameraState } from 'src/components/Camera/helpers/camera-state-helpers';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';

	export let device: Device | undefined;
	export let status: DeviceStatusG2 | undefined;
	export let settings: DeviceSettingsG2 | undefined;
	export let showLiveBadge = false;
	export let compact = false;
	/** Smaller status chips for dashboard camera button cards. */
	export let denseOverlay = false;
	/** Light label styling when rendered over a camera preview. */
	export let onDark = false;
	/** Align labels to the end (right in LTR). Default start (top-left) for dashboard camera headers. */
	export let alignEnd = false;
	/** Use cloud-slash for offline instead of Live/Offline chip (button & device cards). */
	export let useCloudConnectionIcon = false;

	$: cameraKey = device ? getCameraComponentKey(device) : '';
	$: isOnline = !!status?.online;
	$: hasMotion = !!status?.[cameraKey]?.motion;
	$: privacyMode = isCameraPrivacyEnabled(device, status, settings);
	$: streamer = (status?.[cameraKey] as { streamer?: string } | undefined)?.streamer;
	$: cameraState = device && status ? computeCameraState(privacyMode, isOnline, streamer) : null;
	$: baseLabels =
		device && status && settings ? getCameraStatusLabelList(device, status, settings) : [];

	$: motionLabels = baseLabels.map((label) => {
		if (label.name !== 'motion') return label;
		return {
			...label,
			value: hasMotion
				? $format('motion', { default: 'Motion' })
				: $format('status_no', { default: 'No' }),
		};
	});

	$: streamLabels = (() => {
		const labels: typeof baseLabels = [];
		if (!device || !status) return labels;

		if (!isOnline) {
			return labels;
		}

		if (cameraState?.type === 'privacy') {
			labels.push({
				name: 'privacy',
				icon: cameraState.icon || 'fas fa-video-slash',
				value:
					cameraState.heading || $format('camera_privacy_mode', { default: 'Privacy Mode' }),
				iconColor: '--color-warning',
			});
			return labels;
		}

		if (cameraState) {
			labels.push({
				name: 'stream',
				icon: cameraState.icon || 'fal fa-spinner-third fa-spin',
				value: cameraState.heading || $format('camera_starting', { default: 'Starting camera' }),
				iconColor: '--color-muted',
			});
		}

		return labels;
	})();

	$: showConnectionChip = !useCloudConnectionIcon && !!device && !!status;
	/** Button/control cards: cloud-slash when offline only; online uses live dot elsewhere. */
	$: showCloudConnectionIcon = useCloudConnectionIcon && !!device && !!status && !isOnline;
	$: showLiveDot = showConnectionChip && isOnline && showLiveBadge && !cameraState;
	$: showIdleDot = showConnectionChip && (!isOnline || (isOnline && !showLiveBadge && !cameraState));
	$: connectionChipText = showLiveDot
		? $format('label_live', { default: 'Live' })
		: showIdleDot && !isOnline
			? $format('status_offline', { default: 'Offline' })
			: '';

	$: filteredMotionLabels =
		!isOnline && (denseOverlay || useCloudConnectionIcon)
			? motionLabels.filter((label) => label.name !== 'motion')
			: motionLabels;
	$: labelList = isOnline ? [...filteredMotionLabels, ...streamLabels] : filteredMotionLabels;
	$: labelSize = denseOverlay || compact ? 's' : 'm';
	$: connectionIconSize = denseOverlay || compact ? 'xs' : 's';
	$: hasVisibleLabels = showConnectionChip || showCloudConnectionIcon || labelList.length > 0;
</script>

{#if hasVisibleLabels}
	<div
		class="dash-camera-card-info-line"
		class:compact
		class:dense-overlay={denseOverlay}
		class:on-dark={onDark}
		class:align-end={alignEnd}
	>
		<div class="camera-status-labels-row" class:align-end={alignEnd}>
			{#if labelList.length}
				<LabelListV2
					backgroundColor="transparent"
					{labelList}
					size={labelSize}
					a11yProps={{ isInteractive: false }}
					isDefaultCursor
				/>
			{/if}
			{#if showCloudConnectionIcon}
				<div
					class="camera-status-chip camera-cloud-icon"
					role="status"
					aria-label={$format('status_offline', { default: 'Offline' })}
				>
					<Icon
						icon="fa-light fa-cloud-slash"
						size={connectionIconSize}
						class={onDark ? 'text-white' : 'text-color-muted'}
					/>
				</div>
			{/if}
			{#if showConnectionChip}
				<div
					class="camera-status-chip"
					class:live={showLiveDot}
					role="status"
					aria-label={showLiveDot
						? $format('label_live', { default: 'Live' })
						: $format('status_offline', { default: 'Offline' })}
				>
					<span class="camera-status-chip-icon">
						{#if showLiveDot}
							<LiveIndicator />
						{:else if showIdleDot}
							<Icon icon="fal fa-circle" size="xs" class="text-color-muted" />
						{/if}
					</span>
					{#if connectionChipText}
						<span class="camera-status-chip-text">{connectionChipText}</span>
					{/if}
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	.dash-camera-card-info-line {
		min-width: 0;
		max-width: 100%;
	}

	.camera-status-labels-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--spacing-xs);
	}

	.camera-status-labels-row.align-end {
		justify-content: flex-end;
	}

	.dash-camera-card-info-line.align-end {
		margin-left: auto;
	}

	.camera-status-chip {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		min-height: 24px;
		padding: 4px 8px;
		border-radius: var(--radius-small);
		background-color: transparent;
		flex-shrink: 0;
	}

	.camera-status-chip.camera-cloud-icon {
		padding: 2px 4px;
		min-height: 0;
	}

	.dash-camera-card-info-line.compact .camera-status-chip,
	.dash-camera-card-info-line.dense-overlay .camera-status-chip {
		min-height: 20px;
		padding: 2px 4px;
		gap: 2px;
	}

	.camera-status-chip-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		line-height: 0;
	}

	.dash-camera-card-info-line.dense-overlay .camera-status-chip-icon :global(.live-indicator) {
		transform: scale(0.85);
		transform-origin: center;
	}

	.camera-status-chip-text {
		font-size: 12px;
		font-weight: 500;
		line-height: 1;
		white-space: nowrap;
	}

	.dash-camera-card-info-line.compact .camera-status-chip-text,
	.dash-camera-card-info-line.dense-overlay .camera-status-chip-text {
		font-size: 10px;
	}

	.dash-camera-card-info-line.on-dark .camera-status-chip-text {
		color: var(--color-white);
	}

	.dash-camera-card-info-line :global(.label-list-v2) {
		flex-wrap: wrap;
		justify-content: flex-start;
	}

	.dash-camera-card-info-line.align-end .camera-status-labels-row {
		justify-content: flex-end;
	}

	.dash-camera-card-info-line.align-end :global(.label-list-v2) {
		justify-content: flex-end;
	}

	.dash-camera-card-info-line.compact :global(.label-v2) {
		font-size: 10px;
	}

	.dash-camera-card-info-line.dense-overlay :global(.label-list-v2) {
		gap: 2px;
	}

	.dash-camera-card-info-line.dense-overlay :global(.label-v2) {
		min-height: 20px;
		padding: 2px 4px;
	}

	.dash-camera-card-info-line.dense-overlay :global(.label-v2 .icon-grid) {
		font-size: 11px;
		width: 11px;
		height: 11px;
	}

	.dash-camera-card-info-line.on-dark :global(.label-v2) {
		color: var(--color-white);
	}

	.dash-camera-card-info-line.on-dark :global(.label-v2 .icon-grid) {
		color: var(--color-white);
	}
</style>
